import React, {useEffect, useRef, useState} from 'react'
import {useDragAndDropFile} from '../../../hooks/useDragAndDropFile'
import useUploadFile from '../../../hooks/useUploadFile'
import {useSelectedVariantContext} from '../context/SelectedVariantContext'
import {
    actionCopyLayer,
    actionPasteLayer,
    actionRedo,
    actionSetRunningImageCompress,
    actionUndo,
    actionUpdateSizeEditorWrapper,
    removeLayer,
    updateLayerAttribute,
    actionChangeSelectedLayer,
    actionChangeTabDesign,
} from '../context/SelectedVariantContext/action'
import {hideToolbar, curriedMoveLayer as moveLayer, isMacOs} from '../context/SelectedVariantContext/helper'
import {useEditorSides} from '../../../hooks/useEditorSides'
import {useEditorBackground} from '../../../hooks/useEditorBackground'
import {MOVE_LAYER_DIRECTION_KEY, MOVE_LAYER_STEP_NUMBER} from '../context/SelectedVariantContext/constants'
import {useEditorLayers} from '../../../hooks/useEditorLayers'
import {ReactComponent as Upload} from '../../../assets/icons/upload.svg'
import {ReactComponent as ShapeDesign} from '../../../assets/icons/shape-design.svg'
import RemoveBackgroundModal from './remove-background/RemoveBackgroundModal'
import DesignLibraryModal from './design-library/DesignLibraryModal'
import {MockupType} from '../../../constant'
import {useNavigate, useParams} from 'react-router-dom'
import ConditionalRender from '../../../shared/ConditionalRender'
import {hasFeatureTag} from '../../../helpers/settings'
import { buildProductPayload, getGTMAttributes } from '../../../hooks/useGTM'

const EditorWrapper = ({children, classes, isOpenRemoveBackground, toggle, removeBackgroundImageSrc, ...rest}) => {
    const editorWrapperRef = useRef(null)
    const {onDragOver, onDrop} = useDragAndDropFile()
    const {uploadFile} = useUploadFile()
    const layers = useEditorLayers()

    const [loading, setLoading] = React.useState(false)
    const [isOpenImageDesign, setIsOpenImageDesign] = React.useState(false)

    /**
     * @type {State} state
     */
    const {
        state: {
            selectedLayer,
            stageDrag,
            loadingImageLayer,
            runningImageCompress,
            selectedAttribute,
            selectedSide,
            productCreatedType,
            cropMode,
            removeBackgroundMode,
            modalSaveTemplate,
            pickerAttributes,
            product,
            linkableSides = [],
            safeZoneImage,
            pickerVariants,
        },
        dispatch,
    } = useSelectedVariantContext()
    const {pending} = useUploadFile()
    const background = selectedAttribute?.safeZone[selectedSide]
    const hasMockups = product?.mockups && product?.mockups.length > 0
    const [blockChildSide, setBlockChildSide] = useState(false)

    const {ratioDefault} = useEditorBackground()
    const {isOptionalSide} = useEditorSides()
    const navigate = useNavigate()
    const {productId} = useParams()

    /**
     * Handle click create new shape
     * @param {React.MouseEvent<HTMLSpanElement>} event - Event object
     */
    const handleCreateNewShape = (_event) => {
        navigate(`/create-custom-shape?callbackUrl=${productId}`)
    }

    /**
     * Handle click use pre-shape
     * @param {React.MouseEvent<HTMLSpanElement>} event - Event object
     */
    const setTabShape = () => {
        dispatch(actionChangeSelectedLayer(null))
        dispatch(actionChangeTabDesign('shapes'))
    }

    useEffect(() => {
        const itemLink = linkableSides.find((item) => item.linkedAttrIds.includes(selectedAttribute._id))
        if (itemLink !== undefined && itemLink.linkableSides !== undefined) {
            const {childs = []} = itemLink.linkableSides
            if (childs.length > 0) {
                setBlockChildSide(
                    childs.find((item) => item.side === selectedSide) !== undefined &&
                        childs.find((item) => item.side === selectedSide).onLinkable,
                )
            }
        }
    }, [linkableSides, selectedSide])

    useEffect(() => {
        /**
         * Recalculate editor width based on available workspace
         * Grouped reads to avoid layout thrashing (js-batch-dom-css)
         */
        let timerId
        const handleResize = () => {
            const editorWrapper = editorWrapperRef.current
            if (!editorWrapper) return

            const wrapper = document.querySelector('.Step2Container .Wrapper')
            if (!wrapper) return

            setLoading(true)
            let editorWidth = wrapper.offsetWidth

            const leftEditor = wrapper.querySelector('.Left')
            if (leftEditor) {
                const windowWidth = window.innerWidth
                const leftWidth = leftEditor.offsetWidth
                let padding = 200

                if (windowWidth > 1700) padding = 400
                else if (windowWidth > 1500) padding = 300
                editorWidth -= leftWidth + padding
            }

            const toolbarEditor = wrapper.querySelector('.Toolbar')
            if (toolbarEditor) {
                editorWidth -= toolbarEditor.offsetWidth
            }

            dispatch(
                actionUpdateSizeEditorWrapper({width: Math.max(editorWidth, 0), height: editorWrapper.offsetHeight}),
            )

            if (timerId) clearTimeout(timerId)
            timerId = setTimeout(() => {
                setLoading(false)
            }, 500)
        }

        const wrapper = document.querySelector('.Step2Container .Wrapper')
        if (!wrapper) return

        /**
         * ResizeObserver is more robust than window.resize for component layout
         */
        const observer = new ResizeObserver(() => requestAnimationFrame(handleResize))
        observer.observe(wrapper)

        handleResize()

        return () => {
            observer.disconnect()
        }
    }, [dispatch, product?._id])

    const handleDrop = (e) => {
        if (isOptionalSide || !e.dataTransfer.files[0]) return
        dispatch(actionSetRunningImageCompress(true))
        const file = onDrop(e)
        return uploadFile(file)
    }

    const handleClick = () => {
        setIsOpenImageDesign((prev) => !prev)
    }

    const handleFileChange = (event) => {
        const file = event.target.files[0]
        if (file) {
            dispatch(actionSetRunningImageCompress(true))
            return uploadFile(file)
        }
    }

    useEffect(() => {
        if (!selectedLayer) {
            hideToolbar()
        }
    }, [selectedLayer])

    // keyboard events
    useEffect(() => {
        const handleMoveLayer = (key) => {
            const move = moveLayer(key)
            const moveWithDefaultStep = move(MOVE_LAYER_STEP_NUMBER)
            dispatch(updateLayerAttribute(moveWithDefaultStep(selectedLayer)))
        }

        const handleKeyDown = (event) => {
            if (modalSaveTemplate || cropMode || removeBackgroundMode) return
            const isMacOS = isMacOs()
            const {key, ctrlKey, metaKey, keyCode, shiftKey} = event || {}
            const deleteKeysCombination =
                (ctrlKey && key === 'Backspace') ||
                key === 'Delete' ||
                (metaKey && key === 'Backspace') ||
                (metaKey && key === 'Delete')
            if (selectedLayer && deleteKeysCombination) {
                if (selectedLayer.isLock) return
                dispatch(removeLayer(selectedLayer))
                hideToolbar()
            }
            if (
                (ctrlKey && keyCode === 90 && shiftKey) ||
                (isMacOS && keyCode === 90 && (metaKey || ctrlKey) && shiftKey)
            ) {
                hideToolbar()
                dispatch(actionRedo())
            } else if ((ctrlKey && event.key === 'z') || (isMacOS && key === 'z' && (metaKey || ctrlKey))) {
                hideToolbar()
                dispatch(actionUndo())
            } else if ((ctrlKey && key === 'c') || (isMacOS && key === 'c' && (metaKey || ctrlKey))) {
                dispatch(actionCopyLayer(selectedLayer))
            } else if (
                !loadingImageLayer &&
                ((ctrlKey && key === 'v') || (isMacOS && key === 'v' && (metaKey || ctrlKey)))
            ) {
                dispatch(actionPasteLayer())
            } else if (ctrlKey && (key === '+' || key === '-' || key === '=')) {
                event.preventDefault()
            } else if (Object.values(MOVE_LAYER_DIRECTION_KEY).includes(key)) {
                if (selectedLayer && selectedLayer.isLock) return
                handleMoveLayer(key)
            }
        }

        const handleWheel = (event) => {
            if (event.ctrlKey) {
                event.preventDefault()
            }
        }

        const handleResize = (event) => {
            event.preventDefault()
        }

        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('resize', handleResize)
        window.addEventListener('wheel', handleWheel, {passive: false})
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('wheel', handleWheel)
        }
    }, [dispatch, selectedLayer, ratioDefault, loadingImageLayer, cropMode, removeBackgroundMode, modalSaveTemplate])

    const renderBtnUploader = () => {
        return (
            <div className="ClickToUpload" onClick={handleClick}>
                <div>
                    <Upload width={24} height={24} />
                </div>
                {`Drag, drop or upload your design ${
                    productCreatedType === 'regularNoTemplate'
                        ? ''
                        : `(Max ${background.width} x ${background.height} px)`
                }`}
            </div>
        )
    }

    return (
        <div
            {...rest}
            className={classes}
            ref={editorWrapperRef}
            style={{
                opacity: loading ? 0 : 1,
                cursor: stageDrag ? 'grab' : 'default',
            }}
            onDragOver={onDragOver}
            onDrop={handleDrop}
        >
            {children}
            {layers.length === 0 && background && !pending && !runningImageCompress && !blockChildSide && (
                <>
                    <ConditionalRender
                        condition={
                            productCreatedType === MockupType.CUSTOM_SHAPE && hasFeatureTag('custom_shape_step2')
                        }
                        fallback={renderBtnUploader()}
                    >
                        {safeZoneImage && renderBtnUploader()}
                        {!safeZoneImage && (
                            <div className="CustomShapeGuidelines">
                                <div>
                                    <ShapeDesign width={64} height={64} />
                                </div>
                                <div>
                                    <p>
                                        <strong>Create the cut shape for your product.</strong>
                                    </p>
                                    <p>
                                        You can<span onClick={handleCreateNewShape}> create a new shape </span>or
                                        <span onClick={setTabShape}> use one of out pre-made shapes</span>.
                                    </p>
                                    <p>
                                        When finished, click <b>Save shape</b> to continue to the print design.
                                    </p>
                                </div>
                            </div>
                        )}
                    </ConditionalRender>

                    {!hasMockups && (
                        <div className="DescriptionNoTemplate">
                            <div>This product can't be designed in the editor yet.</div>
                            <div>
                                Please use the{' '}
                                <a
                                    className="DescriptionNoTemplate__Template"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={product?.mockup_and_templates_link}
                                >
                                    template
                                </a>{' '}
                                and upload your artwork here.
                            </div>
                        </div>
                    )}
                </>
            )}
            <RemoveBackgroundModal
                isOpen={isOpenRemoveBackground}
                toggle={toggle}
                imageSrc={removeBackgroundImageSrc}
                selectedLayer={selectedLayer}
                dispatch={dispatch}
            />
            {isOpenImageDesign && <DesignLibraryModal isOpen={isOpenImageDesign} toggle={handleClick} />}
        </div>
    )
}

export default EditorWrapper
